import React, { useEffect, useState, useCallback } from "react";

/* ===== Global Styles ===== */
import "./styles/base.css";
import "./App.css";
import "./styles/mobile-fix.css";

/* ===== Components ===== */
import Navigation from "./components/Navigation/Navigation";
import { Footer } from "./components/Footer/Footer";
import { WhatsappFloatButton } from "./components/WhatsappFloatButton/WhatsappFloatButton";
import { QuickAppointment } from "./components/QuickAppointment";
import { AppointmentSuccess } from "./components/AppointmentSuccess";
import { About } from "./components/about";

/* ===== Pages ===== */
import { ContactPage } from "./pages/ContactPage";
import { ArticlesListPage } from "./pages/ArticlesListPage";
import ArticlePage from "./pages/ArticlePage";
import { ServicesPage } from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";

/* ===== Admin ===== */
import AdminLogin from "./pages/admin/AdminLogin";
import AdminPanel from "./pages/admin/AdminPanel";

/* ===== Translations ===== */
import translationsTR from "./data/translationsTR.json";
import translationsEN from "./data/translationsEN.json";
import translationsFR from "./data/translationsFR.json";
import translationsKU from "./data/translationsKU.json";

/* ===== Utils ===== */
import SmoothScroll from "smooth-scroll";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

const App = () => {
  /* ===== STATE MANAGEMENT ===== */
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [landingPageData, setLandingPageData] = useState({});
  const [lang, setLang] = useState(localStorage.getItem("selectedLang") || "TR");
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem("theme") === "dark");

  /* ===== NAVIGATION HELPER ===== */
  const navigateTo = useCallback((page, path = "/") => {
    setCurrentPage(page);
    if (window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
    window.scrollTo(0, 0);
  }, []);

  /* ===== INITIAL ROUTE CHECK (SAYFA YENİLENDİĞİNDE ÇALIŞIR) ===== */
  useEffect(() => {
    const handleLocation = () => {
      const path = window.location.pathname;
      const token = localStorage.getItem('adminToken');

      if (path === "/admin") {
        setCurrentPage(token ? "admin-panel" : "admin-login");
      } else if (path.startsWith("/article/")) {
        const id = path.split("/").pop();
        setSelectedArticleId(id);
        setCurrentPage("article");
      } else if (path.startsWith("/service/")) {
        const id = path.split("/").pop();
        setSelectedServiceId(id);
        setCurrentPage("service-detail");
      } else if (path === "/articles") {
        setCurrentPage("articles");
      } else if (path === "/services") {
        setCurrentPage("services");
      } else if (path === "/contact") {
        setCurrentPage("contact");
      } else {
        setCurrentPage("home");
      }
    };

    handleLocation();
    window.addEventListener('popstate', handleLocation);
    return () => window.removeEventListener('popstate', handleLocation);
  }, []);

  /* ===== THEME & LANGUAGE EFFECTS ===== */
  useEffect(() => {
    localStorage.setItem("selectedLang", lang);
    const translations = { TR: translationsTR, EN: translationsEN, FR: translationsFR, KU: translationsKU };
    setLandingPageData(translations[lang] || translationsTR);
  }, [lang]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  /* ===== ADMIN VIEWS (Özel Render) ===== */
  if (currentPage === "admin-login") {
    return <AdminLogin onLoginSuccess={() => navigateTo("admin-panel", "/admin")} />;
  }

  if (currentPage === "admin-panel") {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setTimeout(() => navigateTo("admin-login", "/admin"), 0);
      return null;
    }
    return (
      <AdminPanel
        onLogout={() => {
          localStorage.removeItem('adminToken');
          navigateTo("home", "/");
        }}
        onNavigate={navigateTo}
        lang={lang}
        setLang={setLang}
        isDarkMode={isDarkMode}
        toggleTheme={() => setIsDarkMode(!isDarkMode)}
        onQuickAppointment={() => navigateTo("quick-appointment", "/quick-appointment")}
      />
    );
  }

  /* ===== PAGE WRAPPER & ROUTER ===== */
  const renderPage = () => {
    switch (currentPage) {
      case "contact":
        return <ContactPage data={landingPageData.Contact} contactInfoData={landingPageData.ContactInfo} lang={lang} isDarkMode={isDarkMode} />;
      case "services":
        return <ServicesPage lang={lang} isDarkMode={isDarkMode} onServiceClick={(id) => { setSelectedServiceId(id); navigateTo("service-detail", `/service/${id}`); }} />;
      case "service-detail":
        return <ServiceDetailPage serviceId={selectedServiceId} lang={lang} isDarkMode={isDarkMode} onBackClick={() => navigateTo("services", "/services")} />;
      case "articles":
        return <ArticlesListPage lang={lang} isDarkMode={isDarkMode} onArticleClick={(id) => { setSelectedArticleId(id); navigateTo("article", `/article/${id}`); }} />;
      case "article":
        return <ArticlePage articleId={selectedArticleId} lang={lang} isDarkMode={isDarkMode} onBackClick={() => navigateTo("articles", "/articles")} />;
      case "quick-appointment":
        return <QuickAppointment lang={lang.toLowerCase()} isDarkMode={isDarkMode} onBack={() => navigateTo("home", "/")} onSuccess={() => navigateTo("appointment-success", "/appointment-success")} />;
      case "appointment-success":
        return <AppointmentSuccess lang={lang.toLowerCase()} isDarkMode={isDarkMode} onBack={() => navigateTo("home", "/")} />;
      default:
        return <About data={landingPageData.About} lang={lang} />;
    }
  };

  return (
    <>
      <Navigation
        lang={lang}
        setLang={setLang}
        isDarkMode={isDarkMode}
        toggleTheme={() => setIsDarkMode(!isDarkMode)}
        onQuickAppointment={() => navigateTo("quick-appointment", "/quick-appointment")}
        onNavigate={navigateTo}
      />
      <main className="content-wrapper">
        {renderPage()}
      </main>
      <Footer lang={lang} isDarkMode={isDarkMode} />
      <WhatsappFloatButton phoneNumber="905054296747" />
    </>
  );
};

export default App;